<template>
  <div class="profile-page-shell donor-profile-page">
    <Edit v-if="Donor" :editDialog="editDialog" :editDonor="Donor"
      @close="editDialog = false" @submit="loadData" />

    <div class="profile-topbar">
      <v-btn text color="primary" class="profile-back-button" to="/donors/list">
        <v-icon left>mdi-arrow-left</v-icon><span>Donors</span>
      </v-btn>
      <div v-if="Donor" class="profile-record-navigation">
        <span>Donor #{{ Donor.localId || Donor.id }}</span>
      </div>
    </div>

    <v-skeleton-loader v-if="loading" type="card, list-item-three-line" class="profile-skeleton" />

    <template v-else-if="Donor && Donor.id">
      <v-alert v-if="Donor.alert" class="profile-alert" color="primary" text border="left"
        icon="mdi-alert-circle-outline">
        <div class="profile-alert__title">Important note</div>
        <div>{{ Donor.note || "This donor has an account alert." }}</div>
      </v-alert>

      <v-card class="profile-hero-card" elevation="0">
        <div class="profile-hero">
          <div class="profile-hero__identity">
            <div class="profile-hero__avatar">{{ initials(Donor.name) }}</div>
            <div>
              <div class="profile-hero__eyebrow">Donor profile</div>
              <h1 class="profile-hero__title">{{ Donor.name }}</h1>
              <div class="profile-hero__meta">
                <span><v-icon size="15">mdi-clock-outline</v-icon> Added {{ Donor.createdAt | moment("from", "now") }}</span>
                <span v-if="Donor.donationInitiated"><v-icon size="15">mdi-web</v-icon> Created online</span>
              </div>
            </div>
          </div>
          <div class="profile-hero__actions">
            <v-btn v-if="checkRightStatus(6)" color="primary" depressed @click="newInvoice">
              <v-icon left size="19">mdi-hand-heart-outline</v-icon>New donation invoice
            </v-btn>
            <v-btn v-if="checkRightStatus(23)" outlined color="primary" @click="editDialog = true">
              <v-icon left size="18">mdi-pencil-outline</v-icon>Edit
            </v-btn>
            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon outlined color="primary" aria-label="More donor actions">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list dense class="action-menu">
                <v-list-item @click="viewOpenInvoice">
                  <v-list-item-icon><v-icon size="19">mdi-file-document-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>Open invoices</v-list-item-title>
                </v-list-item>
                <SyncBalance :DonorId="Donor.id" />
                <Statement :CustomerId="Donor.id" />
                <v-divider />
                <DeleteDonor :donor="Donor" @submit="$router.push('/donors/list')" />
              </v-list>
            </v-menu>
          </div>
        </div>

        <v-divider />
        <v-card-text class="profile-content-grid">
          <div class="profile-info-grid">
            <section class="profile-info-card profile-info-card--full">
              <div class="profile-info-label"><v-icon color="primary" size="18">mdi-account-outline</v-icon>Contact</div>
              <div class="profile-info-name">{{ Donor.name || "Not provided" }}</div>
              <a v-if="donorPhone" :href="`tel:${donorPhone}`" class="profile-info-link">
                <v-icon size="16">mdi-phone-outline</v-icon>{{ donorPhone | USPhoneNumber }}
              </a>
              <span v-else class="profile-info-empty">No phone number</span>
              <a v-if="Donor.email" :href="`mailto:${Donor.email}`" class="profile-info-link">
                <v-icon size="16">mdi-email-outline</v-icon>{{ Donor.email }}
              </a>
              <span v-else class="profile-info-empty">No email address</span>
            </section>

            <section class="profile-info-card profile-info-card--full">
              <div class="profile-info-label"><v-icon color="primary" size="18">mdi-map-marker-outline</v-icon>Address</div>
              <button v-if="Donor.address" type="button" class="profile-address-button" @click="openGoogleMap">
                <strong>{{ Donor.address }}</strong>
                <span>{{ Donor.city }}, {{ Donor.state }} {{ Donor.zip }}</span>
                <small>Open in Google Maps <v-icon size="13">mdi-open-in-new</v-icon></small>
              </button>
              <span v-else class="profile-info-empty">No address provided</span>
            </section>
          </div>

          <aside class="profile-summary-panel">
            <div class="profile-summary-label">Current balance</div>
            <div class="profile-summary-value" :class="{ overdue: isOverLimit }">{{ Donor.balance | currency }}</div>
            <div class="profile-summary-breakdown">
              <div><span>Account type</span><strong>Donor</strong></div>
              <div><span>Donor since</span><strong>{{ Donor.createdAt | moment("MMM YYYY") }}</strong></div>
              <div v-if="Donor.balanceLimit"><span>Balance limit</span><strong>{{ Donor.balanceLimit | currency }}</strong></div>
            </div>
          </aside>
        </v-card-text>
      </v-card>

      <div class="profile-tabs-wrap">
        <v-tabs v-model="active" color="primary" slider-color="primary" show-arrows class="profile-tabs">
          <v-tab v-for="tab in tabs" :key="tab.label"><v-icon left size="18">{{ tab.icon }}</v-icon>{{ tab.label }}</v-tab>
        </v-tabs>
      </div>
      <v-tabs-items v-model="active" touchless class="profile-tab-content">
        <v-tab-item><Dashboard :Donor="Donor" /></v-tab-item>
        <v-tab-item><PaymentMethodReminderList :donor="Donor" :DonorId="Donor.id" /></v-tab-item>
        <v-tab-item><InvoiceList :showAll="true" :DonorId="Donor.id" /></v-tab-item>
        <v-tab-item><PaymentList :DonorId="Donor.id" /></v-tab-item>
        <v-tab-item><Subscription :DonorId="Donor.id" /></v-tab-item>
        <v-tab-item><AttachmentList TypeId="2" :RelationId="Donor.id" /></v-tab-item>
      </v-tabs-items>
    </template>

    <v-alert v-else type="warning" text>We could not load this donor profile.</v-alert>
  </div>
</template>
<script>
const Dashboard = () => import("../components/Dashboard");
const PaymentMethodReminderList = () =>
  import("@/modules/Donors/PaymentInfoCollect/PaymentMethod.vue");
// const PaymentMethod = () =>
//   import("@/modules/Donors/PaymentMethod/components/List.vue");
const InvoiceList = () => import("@/modules/Invoice/components/List.vue");
const AttachmentList = () => import("@/modules/Attachment/components/List");
// const OrderList = () => import("@/modules/Order/InHouse/components/List");
const PaymentList = () => import("@/modules/Invoice/Payment/components/List.vue");
const DeleteDonor = () => import("../components/DeleteDonor");
// const RemoteOrderLink = () => import("@/components/RemoteOrderLink");
const SyncBalance = () => import("@/modules/Donors/components/syncBalance.vue");
const Subscription = () => import("@/modules/Subscription/components/List.vue");
const Statement = () => import("@/modules/Donors/components/Statement");
// const ParentInvoiceCreate = () =>
//   import("@/modules/Customer/components/ParentInvoiceCreate.vue");

// const SearilSearch = () =>
//   import("@/modules/Customer/components/ImeiSearch.vue");

import { mapGetters, mapActions } from "vuex";

import Hashids from "hashids";
const hashids = new Hashids();

const Edit = () => import("@/modules/Donors/components/Edit");
import donorService from "@/modules/Donors/service.js";

export default {
  name: "customer-profile",
  data() {
    return {
      active: null,
      editDialog: false,
      Donor: null,
      loading: true,
    };
  },
  watch: {
    $route(to, from) {
      if (to && from && to.name === from.name) {
        this.loadData();
      }
    },
  },
  components: {
    Edit,
    SyncBalance,
    Subscription,
    Dashboard,
    Statement,
    PaymentMethodReminderList,
    // PaymentMethod,
    InvoiceList,
    AttachmentList,
    PaymentList,
    // OrderList,
    DeleteDonor,
    // ParentInvoiceCreate,
    // Statistics,
    // SearilSearch,
  },
  created() {
    this.loadData();
    this.$events.listen("customerProfileEvent", () => {
      console.log("listen enve")
      // this.$events.emit("invoiceListEvent");
      // this.$events.emit("paymentListEvent");
      // this.$events.emit("studentListEvent");
      // this.$events.emit("subscriptionListEvent");
      this.loadData()
    });
  },
  beforeDestroy() {
    this.$events.remove("customerProfileEvent");
  },
  computed: {
    ...mapGetters("global", ["checkRightStatus"]),
    tabs() {
      return [
        { label: this.$t("labels.dashboard"), icon: "mdi-view-dashboard-outline" },
        { label: "Payment collection", icon: "mdi-credit-card-outline" },
        { label: "Invoices", icon: "mdi-receipt-text-outline" },
        { label: "Payments", icon: "mdi-cash-check" },
        { label: "Subscriptions", icon: "mdi-calendar-sync-outline" },
        { label: "Attachments", icon: "mdi-paperclip" },
      ];
    },
    donorPhone() {
      return this.Donor && this.Donor.phoneNumber
        ? String(this.Donor.phoneNumber).replace(/\D/g, "")
        : "";
    },
    isOverLimit() {
      return Boolean(this.Donor && this.Donor.balanceLimit &&
        Number(this.Donor.balance) > Number(this.Donor.balanceLimit));
    },
  },
  methods: {
    ...mapActions("customer", ["setCustomer"]),
    ...mapActions("global", ["setPageTitle"]),
    initials(name) {
      return String(name || "D")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join("") || "D";
    },
    viewOpenInvoice() {
      const hashId = hashids.encode(this.Donor.id);
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/openInvoice`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    newInvoice() {
      this.$router.push({
        path: `/invoice/create?DonorId=${this.Donor.id}`,
      });
    },
    async loadData() {
      let id = this.$route.params.id;
      this.loading = true;
      this.$Progress.start();
      return donorService
        .getById(id)
        .then((response) => {
          this.Donor = response.data;
          document.title = `taleem - ${this.Donor.name}`
          this.$Progress.finish();
        })
        .catch(() => {
          this.Donor = null;
          this.$Progress.finish();
        })
        .finally(() => {
          this.loading = false;
        });

      // await this.setCustomer(id);
      // this.setPageTitle(this.Donor.name);
      // console.log("Donor", this.Donor);
    },
    openGoogleMap() {
      window.open(
        `https://www.google.com/maps/place/${this.Donor.address} ${this.Donor.city},${this.Donor.state} ${this.Donor.zip}`,
        "_blank"
      );
    },
    sendEmail() {
      let routeData =
        "https://mail.google.com/mail/?view=cm&fs=1&to=" + this.Donor.email;
      window.open(routeData, "newwindow", "width=800, height=950");
    },
    closeEdit() {
      this.editDialog = false;
    },
    updated() {
      this.closeEdit();
      this.loadData();
    },
  },
};
</script>
